import { query } from '@angular/animations';
import { CommonModule, DatePipe, JsonPipe, ɵnormalizeQueryParams } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule, MatDateRangeInput } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { IgxGridModule } from '@infragistics/igniteui-angular';
import { IgxGridComponent, IgxTabsModule } from '@infragistics/igniteui-angular';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { AuthService } from 'src/app/core/services/auth-security/auth.service';
import { MessageService } from 'src/app/core/services/broadcast/message-service';
import { DataService } from 'src/app/core/services/data/data.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { ParkerStateService } from 'src/app/core/services/state/parker-state.service';
import {
  IApnMassActivationSpaceEmailLink,
  IApnSpaceEmailLink,
  ILotList,
  IParkerActivationReturnData,
  IParkersReadyForactivation,
  IParkingAgreementReportList,
  IParkingAgreementReportQueryParams,
} from 'src/app/shared/interfaces';
import { HeaderTitleLotDataComponent } from 'src/app/shared/ui/header-title-lot-data/header-title-lot-data.component';

@Component({
  selector: 'app-rpt-parking-agreement',
  templateUrl: './rpt-parking-agreement.component.html',
  styleUrl: './rpt-parking-agreement.component.scss',
  standalone: true,
  imports: [
    MatSlideToggleModule,
    MatExpansionModule,
    MatIconModule,
    CommonModule,
    MatTabsModule,
    MatSnackBarModule,
    MatTableModule,
    MatCardModule,
    MatOptionModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatDateRangeInput,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    IgxGridModule,
    IgxTabsModule,
    IgxGridComponent,
    HeaderTitleLotDataComponent,
    MatSnackBarModule,
  ],
  providers: [provideNativeDateAdapter()],
})
export class RptParkingAgreementComponent implements OnInit {
  userName: string = '';
  querySubmitted: boolean = false;
  lotNumber: string = '';
  selectedLot: string = '';
  gridFontSize: number = 12;
  parkingAgreementReportList: IParkingAgreementReportList[] = [];
  // displayedColumns: string[] = [
  //   'parkerId',
  //   'firstName',
  //   'company',
  //   'agreementStatus',
  //   'dateLastUpdated',
  //   'userNameLastUpdate',
  // ];
  //dataSource = new MatTableDataSource(this.parkingAgreementReportList);

  agreementSearchForm = new FormGroup({
    agreementStatus: new FormControl('ALL', []),
    startDate: new FormControl('', []),
    endDate: new FormControl('', []),
  });

  //SERVICES
  private dataService = inject(DataService);
  public _appGeneralState = inject(AppGeneralStateService);
  private _destroyRefService = inject(DestroyRef);
  _messageService = inject(MessageDialogService);
  _snackBar = inject(MatSnackBar);

  constructor() {
    this._appGeneralState.lotNumberSubject.subscribe({
      next: (v) => this.onLotNumberChange(v),
    });
  }

  ngOnInit(): void {}

  onLotNumberChange(lotNumber: any) {
    if (this.parkingAgreementReportList.length > 0) {
      this._messageService.snackBarTopCenterTimed(
        'The lot number was changed and the data for the previous lot has been cleared.',
        3000,
      );
    }

    this.parkingAgreementReportList = [];
  }

  onFindParkingAgreeements(): void {
    const queryString: string = this.buildQueryString();
    this.getParkingAgreementList(queryString);
  }

  onClearSearchCriteria(): void {
    this.agreementSearchForm.setValue({
      agreementStatus: 'ALL',
      startDate: '',
      endDate: '',
    });
  }

  buildQueryString(): string {
    let datePipe: DatePipe = new DatePipe('en-US');
    var queryString: string = '';
    queryString = `lotNumber=${this._appGeneralState.currentLotNumber}`;

    const agreementStatus: string = this.agreementSearchForm.get('agreementStatus').value;
    let startDate: string = this.agreementSearchForm.get('startDate').value;
    let endDate: string = this.agreementSearchForm.get('endDate').value;

    if (agreementStatus == 'ALL') {
      queryString += `&agreementStatus=NULL`;
    } else {
      queryString += `&agreementStatus=${agreementStatus}`;
    }

    if (startDate == '' || startDate == null) {
      queryString += `&startDate=NULL`;
    } else {
      startDate = datePipe.transform(startDate, 'M/dd/yyyy');
      queryString += `&startDate=${startDate}`;
    }

    if (endDate == '' || endDate == null) {
      queryString += `&endDate=NULL`;
    } else {
      endDate = datePipe.transform(endDate, 'M/dd/yyyy');
      queryString += `&endDate=${endDate}`;
    }

    return queryString;
  }

  getParkingAgreementList(queryString: string) {
    this.displaySearchMessage();
    this.dataService
      .getParkingAgreementReportList(queryString)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.parkingAgreementReportList = res;
        },
        error: (err: any) => {
          this._snackBar.dismiss();
          this._messageService.snackBarTopCenterTimed(
            'An unexpected error occurred while loading the Parking Agreement report.  Please try again.',
            5000,
          );
        },
        complete: () => {
          this._snackBar.dismiss();
          this.setAgreementStatusText();
          if (this.parkingAgreementReportList.length == 0) {
            this._messageService.snackBarTopCenterTimed(
              'No Parking Agreements were located with this search criteria.',
              5000,
            );
          }
        },
      });
  }

  setAgreementStatusText(): void {
    let ii: number = 0;
    let datePipe: DatePipe = new DatePipe('en-US');

    for (ii = 0; ii < this.parkingAgreementReportList.length; ii++) {
      this.parkingAgreementReportList[ii].agreementStatusDisplay = this.transposeAgreementStatus(
        this.parkingAgreementReportList[ii].agreementStatus,
      );
      this.parkingAgreementReportList[ii].dateLastUpdatedDisplay = datePipe.transform(
        this.parkingAgreementReportList[ii].dateLastUpdated,
        'M/dd/yyyy',
      );
    }
  }

  transposeAgreementStatus(statusId: number): string {
    let statusText: string = '';

    if (statusId == 0) {
      statusText = 'Notification Sent/Pending Signature';
    } else if (statusId == 1) {
      statusText = 'Exempt from Agreement';
    } else if (statusId == 2) {
      statusText = 'Removed from Exempt & Invite Sent';
    } else if (statusId == 3) {
      statusText = 'Expired Invitation';
    } else if (statusId == 4) {
      statusText = 'Parker Termed-Invite Cancelled';
    } else if (statusId == 5) {
      statusText = 'Parker Viewed Agreement';
    } else if (statusId == 6) {
      statusText = 'Agreement Signed';
    } else if (statusId == 7) {
      statusText = 'Email Resent/Pending';
    } else if (statusId == 8) {
      statusText = 'Notification Failed';
    } else if (statusId == 9) {
      statusText = 'Removed from Exempt - No Invite Sent';
    } else {
      statusText = 'Error retrieving Status';
    }

    return statusText;
  }

  displaySearchMessage(): void {
    const verticalPosition: MatSnackBarVerticalPosition = 'top';

    this._snackBar.open('Loading Parking Agreement Report...', '', { verticalPosition: verticalPosition });
  }
}
